import {
  Autocomplete,
  Checkbox,
  TextField,
  Divider,
  Stack,
  Grid,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Button,
  ButtonBase,
} from '@material-ui/core'
import { useState, useEffect } from 'react'
import { fetchHPOTerms } from 'src/api/filters/hpo'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'

// A picked HPO term in the shape the rest of the app stores.
const toTermValue = (term) =>
  term.value
    ? term
    : {
        value: term.hpo_id,
        label: `${term.name} (${term.hpo_id})`,
        name: term.name,
        hpo_id: term.hpo_id,
        synonyms: term.synonyms || [],
      }

const termKey = (term) => term?.value || term?.hpo_id

/**
 * `compact` (New analysis panel): one small search box, no select-all row and no
 * empty "No HPO terms selected" box. Picked terms show as chips under the box and
 * the search clears after each pick. `inputId` sets the id of the search input.
 */
function HPOFilter({ hpoTerms = [], setHpoTerms, compact = false, inputId }) {
  const [input, setInput] = useState('')
  const [hpoOptions, setHpoOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [hoveredOption, setHoveredOption] = useState(null)
  const [expandedSynonyms, setExpandedSynonyms] = useState({})

  const handleFetchHPOTerms = async (searchTerm) => {
    setLoading(true)
    try {
      const params = {}
      if (searchTerm) {
        params.search = searchTerm
      }

      const results = await fetchHPOTerms(params)
      setHpoOptions(results)
    } catch (error) {
      console.error('Error fetching HPO terms:', error)
      setHpoOptions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input.length >= 2) {
        handleFetchHPOTerms(input)
      } else {
        setHpoOptions([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [input])

  const getOptionLabel = (option) => {
    if (option === 'select-all-option') return ''
    if (!option) return ''
    if (typeof option === 'string') return option

    // Include synonyms in the searchable text
    const synonymsText = option.synonyms?.length > 0 ? ` (${option.synonyms.join(', ')})` : ''

    return option.label || `${option.name} (${option.hpo_id})${synonymsText}`
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      const newHpoTerms = [...hpoTerms]
      hpoOptions.forEach((term) => {
        if (!hpoTerms.some((t) => t?.value === term?.hpo_id)) {
          newHpoTerms.push({
            value: term.hpo_id,
            label: `${term.name} (${term.hpo_id})`,
            name: term.name,
            hpo_id: term.hpo_id,
            synonyms: term.synonyms,
          })
        }
      })
      setHpoTerms(newHpoTerms)
    } else {
      const filteredTerms = hpoTerms.filter((term) => !hpoOptions.some((o) => o?.hpo_id === term?.value))
      setHpoTerms(filteredTerms)
    }
  }

  const areAllCurrentResultsSelected = () => {
    return hpoOptions.length > 0 && hpoOptions.every((term) => hpoTerms.some((t) => t?.value === term?.hpo_id))
  }

  const getOptions = () => {
    if (hpoOptions.length === 0) return []
    return compact ? hpoOptions : ['select-all-option', ...hpoOptions]
  }

  const handleClearInput = () => {
    setInput('')
    setHpoOptions([])
  }

  if (compact) {
    if (hpoTerms === null || !setHpoTerms) return null
    const removeTerm = (term) => setHpoTerms(hpoTerms.filter((t) => termKey(t) !== termKey(term)))
    return (
      <Box>
        <Autocomplete
          id={inputId}
          multiple
          size="small"
          options={getOptions()}
          loading={loading}
          disableClearable
          forcePopupIcon={false}
          filterOptions={(x) => x}
          inputValue={input}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'reset' || reason === 'blur') return
            setInput(newInputValue)
          }}
          clearOnBlur={false}
          getOptionLabel={getOptionLabel}
          value={hpoTerms || []}
          onChange={(_, val) => {
            setHpoTerms(val.map(toTermValue))
            handleClearInput()
          }}
          isOptionEqualToValue={(option, value) => option?.hpo_id === termKey(value)}
          renderInput={(params) => {
            const { InputProps, ...rest } = params
            return (
              <TextField
                {...rest}
                size="small"
                variant="outlined"
                placeholder="Search HPO by name or ID"
                InputProps={{
                  ...InputProps,
                  sx: { fontSize: 14.5 },
                  startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', ml: 0.5, mr: 0.5 }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {loading ? <CircularProgress color="inherit" size={14} /> : null}
                      {input.length > 0 && (
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleClearInput()
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
                onKeyDown={(e) => {
                  // Backspace in an empty box would remove the last term.
                  if (e.key === 'Backspace' && input.length === 0) e.stopPropagation()
                }}
              />
            )
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.hpo_id}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: selected ? 600 : 400, minWidth: 0 }} noWrap>
                    {option.name}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontFamily: 'fontFamilyMono', fontSize: 12.5, color: 'text.secondary', flexShrink: 0 }}
                  >
                    {option.hpo_id}
                  </Typography>
                </Box>
                {option.synonyms?.length > 0 && (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap>
                    {option.synonyms.slice(0, 3).join(', ')}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          noOptionsText={
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {input.length < 2 ? 'Type at least 2 characters' : 'No matching HPO terms'}
            </Typography>
          }
          ListboxProps={{ style: { maxHeight: 360 } }}
        />
        {hpoTerms.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1 }}>
            {hpoTerms.map((term) => {
              const name = term.name || term.label || ''
              return (
                <Chip
                  key={termKey(term)}
                  size="small"
                  label={name}
                  title={term.hpo_id ? `${name} (${term.hpo_id})` : name}
                  onDelete={() => removeTerm(term)}
                  sx={{ maxWidth: 280, fontSize: 13 }}
                />
              )
            })}
            {hpoTerms.length > 1 && (
              <ButtonBase
                disableRipple
                onClick={() => setHpoTerms([])}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'text.secondary',
                  borderRadius: 0.5,
                  px: 0.5,
                  '&:hover': { textDecoration: 'underline' },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                Clear all
              </ButtonBase>
            )}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Stack direction="column" spacing={2} divider={<Divider />}>
      {hpoTerms !== null && setHpoTerms && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Selected HPO Terms ({hpoTerms.length})
              </Typography>
              {hpoTerms.length > 0 && (
                <IconButton size="small" onClick={() => setHpoTerms([])} title="Clear all HPO terms">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                maxHeight: '200px',
                overflowY: 'auto',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              {hpoTerms.length > 0 ? (
                hpoTerms.map((term) => {
                  // Only show the name, not the id in parentheses
                  const label = term.name || term.label || ''
                  const truncatedLabel = label.length > 32 ? `${label.slice(0, 29)}...` : label
                  const needsTooltip = label.length > 32

                  return (
                    <Tooltip key={term.value || term.hpo_id} title={needsTooltip ? label : ''}>
                      <Chip
                        label={truncatedLabel}
                        onDelete={() => {
                          const newTerms = hpoTerms.filter((t) => (t.value || t.hpo_id) !== (term.value || term.hpo_id))
                          setHpoTerms(newTerms)
                        }}
                        sx={{
                          maxWidth: '300px',
                          '& .MuiChip-label': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                          transition: 'background-color 0.2s',
                        }}
                      />
                    </Tooltip>
                  )
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No HPO terms selected
                </Typography>
              )}
            </Box>
          </Box>
          <Autocomplete
            multiple
            options={getOptions()}
            loading={loading}
            disableCloseOnSelect
            disableClearable
            filterOptions={(x) => x}
            inputValue={input}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset' || reason === 'blur') return
              setInput(newInputValue)
            }}
            clearOnBlur={false}
            blurOnSelect={false}
            renderInput={(params) => {
              const { InputProps, ...rest } = params
              return (
                <Grid sx={{ display: 'flex' }}>
                  <TextField
                    {...rest}
                    label="Search HPO terms by name or ID"
                    variant="outlined"
                    placeholder="Type at least 2 characters to search..."
                    InputProps={{
                      ...InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {InputProps.endAdornment}
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {input.length > 0 && (
                            <IconButton
                              size="small"
                              aria-label="Clear search input"
                              onClick={(e) => {
                                e.stopPropagation()
                                setInput('')
                                setHpoOptions([])
                              }}
                              tabIndex={0}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && input.length === 0 && hpoTerms.length > 0) {
                        e.stopPropagation()
                      }
                    }}
                  />
                </Grid>
              )
            }}
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                style={{
                  transition: 'background-color 0.2s',
                }}
              >
                {option === 'select-all-option' ? (
                  <Box
                    sx={{
                      width: '100%',
                      borderBottom: 1,
                      borderColor: 'divider',
                      pb: 1,
                      backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={areAllCurrentResultsSelected()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          '&:hover': { color: 'primary.main' },
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: areAllCurrentResultsSelected() ? 'primary.main' : 'inherit',
                        }}
                      >
                        Select All Results ({hpoOptions.length})
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={Array.isArray(hpoTerms) && hpoTerms.some((t) => t?.value === option?.hpo_id)}
                        sx={{
                          '&:hover': { color: 'primary.main' },
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: hpoTerms.some((t) => t?.value === option?.hpo_id) ? 500 : 400,
                            color: hpoTerms.some((t) => t?.value === option?.hpo_id) ? 'primary.main' : 'inherit',
                          }}
                        >
                          {option?.name}
                        </Typography>
                        {option?.hpo_id && <Chip label={option.hpo_id} size="small" sx={{ height: 20, mt: 0.5 }} />}
                        {option?.definition && (
                          <Tooltip title={option.definition} placement="bottom-start">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'help',
                              }}
                            >
                              {option.definition}
                            </Typography>
                          </Tooltip>
                        )}
                        {option?.synonyms && option.synonyms.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                              }}
                            >
                              Synonyms:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {(expandedSynonyms[option.hpo_id] ? option.synonyms : option.synonyms.slice(0, 3)).map(
                                (synonym, index) => (
                                  <Chip
                                    key={index}
                                    label={synonym}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: '0.7rem',
                                      backgroundColor: (theme) =>
                                        theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.light',
                                    }}
                                  />
                                ),
                              )}
                              {option.synonyms.length > 3 && (
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setExpandedSynonyms((prev) => ({
                                      ...prev,
                                      [option.hpo_id]: !prev[option.hpo_id],
                                    }))
                                  }}
                                  endIcon={
                                    expandedSynonyms[option.hpo_id] ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )
                                  }
                                  sx={{
                                    minWidth: 'auto',
                                    padding: '0 4px',
                                    height: 16,
                                    fontSize: '0.7rem',
                                    textTransform: 'none',
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.light',
                                    '&:hover': {
                                      backgroundColor: (theme) =>
                                        theme.palette.mode === 'dark' ? 'primary.main' : 'primary.light',
                                    },
                                  }}
                                >
                                  {expandedSynonyms[option.hpo_id]
                                    ? 'Show less'
                                    : `+${option.synonyms.length - 3} more`}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </li>
            )}
            getOptionLabel={getOptionLabel}
            value={hpoTerms || []}
            onChange={(_, val) => {
              const filteredVal = val.filter((v) => v !== 'select-all-option')
              const formattedVal = filteredVal.map((term) => {
                if (term.value) return term // Already formatted
                return {
                  value: term.hpo_id,
                  label: `${term.name} (${term.hpo_id})`,
                  name: term.name,
                  hpo_id: term.hpo_id,
                  synonyms: term.synonyms || [],
                }
              })
              setHpoTerms(formattedVal)
            }}
            isOptionEqualToValue={(option, value) => {
              if (option === 'select-all-option') return false
              return option?.hpo_id === value?.value || option?.hpo_id === value?.hpo_id
            }}
            noOptionsText={
              <Box sx={{ py: 2, textAlign: 'center' }}>
                {input.length < 2 ? (
                  <Typography color="text.secondary">Type at least 2 characters to search</Typography>
                ) : (
                  <Typography color="text.secondary">No results found</Typography>
                )}
              </Box>
            }
            ListboxProps={{
              style: {
                maxHeight: '400px',
              },
            }}
          />
        </>
      )}
    </Stack>
  )
}

export default HPOFilter
